/**
 * TLF Analytics React Hook - Automatic page tracking on route changes
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView, trackEngagement } from '../helpers/tlfAnalytics';
import { getConfig } from '../config/environment';

/**
 * React hook for automatic page view tracking on route changes
 * Also tracks page engagement (time spent) when navigating away
 * 
 * Usage:
 * function App() {
 *   useTlfPageTracking();
 *   return <Routes>...</Routes>;
 * }
 */
export const useTlfPageTracking = () => {
  const location = useLocation();
  const pageLoadTime = useRef(Date.now());
  const lastPathname = useRef(location.pathname);

  const clicksRef = useRef([]);
  const maxScrollRef = useRef(0);

  // Global interaction event listeners
  useEffect(() => {
    const handleGlobalClick = (e) => {
      if (clicksRef.current.length >= 100) return;
      clicksRef.current.push({
        x: e.pageX,
        y: e.pageY,
        w: window.innerWidth,
        h: window.innerHeight,
        tag: e.target ? e.target.tagName.toLowerCase() : 'unknown'
      });
    };

    let scrollTimeout;
    const handleScroll = () => {
      if (!scrollTimeout) {
        scrollTimeout = setTimeout(() => {
          scrollTimeout = null;
          const scrollTop = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
          const windowHeight = window.innerHeight;
          const docHeight = document.documentElement.scrollHeight;
          const totalScrollable = docHeight - windowHeight;
          
          if (totalScrollable > 0) {
            const depthPercentage = ((scrollTop + windowHeight) / docHeight) * 100;
            if (depthPercentage > maxScrollRef.current) {
              maxScrollRef.current = depthPercentage;
            }
          } else {
            maxScrollRef.current = 100;
          }
        }, 200);
      }
    };

    window.addEventListener('click', handleGlobalClick, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Stretchy iframe integration: if running inside a dashboard heatmap iframe overlay,
    // postMessage document dimensions to parent on load and size changes with throttling and deduplication.
    let resizeObserver;
    let resizeTimer = null;
    if (window.self !== window.top) {
      let lastSentHeight = 0;
      let throttleTimer = null;

      const sendDimensions = () => {
        try {
          const root = document.getElementById('root') || document.body;
          const scrollHeight = Math.max(
            root?.scrollHeight || 0,
            document.body?.scrollHeight || 0,
            document.documentElement?.scrollHeight || 0
          );
          // Only send if height changed by at least 15px to avoid endless 1px layout thrashing / feedback loops
          if (scrollHeight > 100 && Math.abs(scrollHeight - lastSentHeight) >= 15) {
            lastSentHeight = scrollHeight;
            window.parent.postMessage({
              type: 'tlf_heatmap_resize',
              scrollHeight
            }, '*');
          }
        } catch { /* ignore cross-origin child postMessage blocks */ }
      };

      const throttledSend = () => {
        if (!throttleTimer) {
          throttleTimer = setTimeout(() => {
            throttleTimer = null;
            sendDimensions();
          }, 350);
        }
      };

      if (typeof ResizeObserver !== 'undefined') {
        const target = document.getElementById('root') || document.body;
        if (target) {
          resizeObserver = new ResizeObserver(() => throttledSend());
          resizeObserver.observe(target);
        }
      }

      window.addEventListener('resize', throttledSend, { passive: true });
      // Initial dimension reports after DOM settles
      sendDimensions();
      resizeTimer = setTimeout(sendDimensions, 1000);
    }

    // Initial check
    setTimeout(handleScroll, 500);

    return () => {
      window.removeEventListener('click', handleGlobalClick);
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) clearTimeout(scrollTimeout);
      if (resizeTimer) clearTimeout(resizeTimer);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    // Track engagement for previous page
    if (lastPathname.current !== location.pathname) {
      const duration = Date.now() - pageLoadTime.current;
      if (duration > 1000) { // Only track if > 1 second
        trackEngagement(duration, {
          pathname: lastPathname.current,
          clicks: [...clicksRef.current],
          maxScroll: Math.round(maxScrollRef.current)
        });
      }
      
      // Reset for new page
      clicksRef.current = [];
      maxScrollRef.current = 0;
      pageLoadTime.current = Date.now();
      lastPathname.current = location.pathname;
    }

    // Track page view for current page
    trackPageView(location.pathname, {
      search: location.search,
      hash: location.hash
    });

    // Stretchy iframe integration: notify parent dashboard of route changes inside the iframe
    if (window.self !== window.top) {
      try {
        window.parent.postMessage({
          type: 'tlf_heatmap_route_change',
          pathname: location.pathname
        }, '*');
      } catch { /* ignore cross-origin postMessage blocks */ }
    }
  }, [location.pathname, location.search]);

  // Track engagement on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      const duration = Date.now() - pageLoadTime.current;
      if (duration > 1000) {
        // Use sendBeacon for reliable delivery on unload.
        // IMPORTANT: build payload through the same shape as trackEvent so
        // UTM attribution (from sessionStorage / captured on first PageView
        // of this session) is included — otherwise the final PageEngagement
        // beacon on tab close loses its campaign attribution, which matters
        // for /partner/<code> QR-code landings where UTMs only reached the
        // client via the tlf_utm cookie on first hit.
        const { backendUrl } = getConfig();
        const apiUrl = backendUrl || '';
        if (apiUrl) {
          const sessionId = localStorage.getItem('tlf_analytics_session');
          // Pull captured UTM attribution from sessionStorage (same source
          // getUtmAttribution uses) so we don't have to import the helper
          // (which is module-state-protected). If absent, this just omits UTMs
          // — same behavior as before, no regression.
          let utmAttribution = null;
          try {
            const stored = sessionStorage.getItem('tlf_utm_attribution');
            if (stored) utmAttribution = JSON.parse(stored);
          } catch { /* ignore */ }

          const payload = {
            eventType: 'PageEngagement',
            sessionId,
            pageUrl: window.location.href,
            pagePath: location.pathname,
            duration,
            customData: {
              unload: true,
              clicks: clicksRef.current,
              maxScroll: Math.round(maxScrollRef.current),
              ...(utmAttribution || {}),
            }
          };

          // Wrap in Blob with correct Content-Type for sendBeacon
          const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
          navigator.sendBeacon(
            `${apiUrl}/analytics/track`,
            blob
          );
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [location.pathname]);
};

export default useTlfPageTracking;
